import jmespath from "jmespath";
import jsonata from "jsonata";
import { find, get } from "lodash";
import { Buffer } from "buffer";
import { notification } from "antd";
import moment from "moment";

export const agGridKey = () => {
    // eslint-disable-next-line max-len
    return 'VXNpbmdfdGhpc197QUdfR3JpZH1fRW50ZXJwcmlzZV9rZXlfe0FHLTA5OTM4N31faW5fZXhjZXNzX29mX3RoZV9saWNlbmNlX2dyYW50ZWRfaXNfbm90X3Blcm1pdHRlZF9fX1BsZWFzZV9yZXBvcnRfbWlzdXNlX3RvX2xlZ2FsQGFnLWdyaWQuY29tX19fRm9yX2hlbHBfd2l0aF9jaGFuZ2luZ190aGlzX2tleV9wbGVhc2VfY29udGFjdF9pbmZvQGFnLWdyaWQuY29tX19fe1N3ZWZ0X0xMQ31faXNfZ3JhbnRlZF9hX3tTaW5nbGVfQXBwbGljYXRpb259X0RldmVsb3Blcl9MaWNlbnNlX2Zvcl90aGVfYXBwbGljYXRpb25fe1N3ZWZ0fV9vbmx5X2Zvcl97MX1fRnJvbnQtRW5kX0phdmFTY3JpcHRfZGV2ZWxvcGVyX19fQWxsX0Zyb250LUVuZF9KYXZhU2NyaXB0X2RldmVsb3BlcnNfd29ya2luZ19vbl97U3dlZnR9X25lZWRfdG9fYmVfbGljZW5zZWRfX197U3dlZnR9X2hhc19ub3RfYmVlbl9ncmFudGVkX2FfRGVwbG95bWVudF9MaWNlbnNlX0FkZC1vbl9fX1RoaXNfa2V5X3dvcmtzX3dpdGhfe0FHX0dyaWR9X0VudGVycHJpc2VfdmVyc2lvbnNfcmVsZWFzZWRfYmVmb3JlX3sxMl9TZXB0ZW1iZXJfMjAyN31fX19fW3YzXV9bMDFdX01UZ3lNRGN3TXpZd01EQXdNQT09MjVmMzQ2YmE3ZWJmZmIwOTY4NDJkZmEzYjQzNmVkZGU=';
};

export const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/** @param {string} displayName */
export const generateNameFromDisplayName = (displayName) => displayName?.trim().toLowerCase().replace(/[^a-zA-Z0-9]+/g, "-");

export const getTreeFromFlatOptions = ({ flatOptions, propertyToDisplay, onlyEnableLowestOptions, propertyToSave = 'hierarchy' }) => {
    const tree = [];
    flatOptions.sort((optionA, optionB) => (optionA.hierarchy > optionB.hierarchy) ? 1 : -1);

    flatOptions.forEach((flatOption) => {
        const optionPathArr = flatOption?.hierarchy?.split("/").slice(0, -1);
        let currentLevel = tree;
        if (!currentLevel.children) {
            optionPathArr?.forEach((optionPathPart) => {
                const existingPath = currentLevel.find((treeObj) => treeObj.partName === optionPathPart);
                if (existingPath && existingPath?.partName !== flatOption[propertyToDisplay]) {
                    currentLevel = existingPath.children;
                    if (onlyEnableLowestOptions) {
                        existingPath.disabled = true;
                    }
                } else {
                    const newPart = {
                        title: flatOption[propertyToDisplay],
                        partName: optionPathPart,
                        value: flatOption[propertyToSave],
                        children: [],
                        hierarchy: flatOption.hierarchy,
                        key: flatOption[propertyToSave]
                    };
                    currentLevel.push(newPart);
                    currentLevel = newPart.children;
                }
            });
        }
    });

    return tree;
};


export const getDefaultExpandedKeys = ({ treeData, defaultExpandedLevel = 0 }) => {
    const defaultExpandedKeys = [];
    let currentExpandedLevel = defaultExpandedLevel;
    let currentTreeData = treeData;
    while (currentExpandedLevel > 0) {
        const currentLevelKeys = currentTreeData.map((treeNode) => treeNode.key);
        defaultExpandedKeys.push(...currentLevelKeys);
        currentTreeData = [...currentTreeData.map((treeNode) => treeNode.children ?? [])].flat();
        currentExpandedLevel = currentExpandedLevel - 1;
    }
    return defaultExpandedKeys;
};


export const isObject = (val) => typeof val === 'object' && !Array.isArray(val) && val !== null;

export const asMillis = date => {
    if (typeof date === 'string') {
        const parsedDate = Date.parse(date);
        return isNaN(parsedDate) ? null : parsedDate;
    } else if (typeof date === 'number') {
        return date; // Assume numbers are already milliseconds
    }
    return null; // Non-string, non-number inputs are set to null
};

export const formulaProcessors = {
    jmespath(obj, formulaValue) {
        try {
            const response = jmespath.search(obj, formulaValue);
            if (response === undefined) {
                return null;
            }
            return response;
        } catch (e) {
            return null;
        }
    },
    jsonata(obj, expressionStr) {
        try {
            const expression = jsonata(expressionStr);
            expression.registerFunction("asMillis", asMillis);
            const response = expression.evaluate(obj);
            if (response === undefined) {
                return null;
            }
            return response;
        } catch (e) {
            return null;
        }
    }
};

export const getRelatedEntityListFromAttributeList = (attributeList) => {
    return [
        ...new Set(attributeList?.filter((attribute) => {
            if (attribute.baseType === "Relationship" && attribute.relation) {
                return true;
            }
            return !!((attribute.format === "Dropdown" || attribute.format === "NestedDropdown") && attribute.specifications?.editorParams?.dropdownId);
        }).map((attribute) => {
            if (attribute.baseType === "Relationship") {
                return attribute.relation;
            }
            if (attribute.format === "Dropdown" || attribute.format === "NestedDropdown") {
                const { dropdownId = null } = attribute.specifications.editorParams;
                return generateNameFromDisplayName(dropdownId);
            }
            return null;
        }))
    ];
};

/**
 * Comparator function to use when sorting objects by their createdDate property
 * @param workspaceData [Array]
 * @returns {Array}
 */
export const compareByCreatedDate = (workspaceData) => {
    return workspaceData?.sort((objA, objB) => {
        const dateA = new Date(objA.createdDate).getTime();
        const dateB = new Date(objB.createdDate).getTime();

        if (!objA.createdDate) {
            return 0;
        }
        if (!objB.createdDate) {
            return -1;
        }

        return dateB - dateA;
    });
};

export const getCurrentSearchParams = () => (new URL(document.location)).searchParams;

export const sortArray = (array, key) => (array.sort((valueA, valueB) => valueA[key]?.localeCompare(valueB[key])));

export const sortArrayBySequence = (array) => (
    array.sort((itemA, itemB) => {
        const sequenceA = itemA?.sequence;
        const sequenceB = itemB?.sequence;

        if (sequenceA === null && sequenceB === null) {
            return 0;
        }

        if (sequenceA === null) {
            return 1;
        }

        if (sequenceB === null) {
            return -1;
        }

        return Number(sequenceA) - Number(sequenceB);
    })
);

export const findInList = (list, predicate) => find(list, predicate) ?? null;

export const base64Encode = (stringToEncode) => Buffer.from(stringToEncode).toString('base64');

export const base64Decode = (stringToDecode) => Buffer.from(stringToDecode, 'base64').toString('ascii');

/**
 * Use AntD notification to show error messages
 * @param {string} message
 * @param {string} description
 * @param {'error'|'success'|'info'|'warning'} type
 */
export const openNotificationWithIcon = (type, message, description) => {
    const finalType = ['error', 'success', 'info', 'warning'].includes(type) ? type : 'error';
    notification[finalType]({
        message,
        description,
    });
};

/**
 * Remove objects from the list that have duplicate values for the keyProperty.
 * The last object that matches the value is kept.
 * @param objectList
 * @param keyProperty
 * @returns {[]}
 */
export const getUniqueObjList = ({ objectList = [], keyProperty = null } = {}) => [...new Map(objectList?.map(object =>
    [object?.[keyProperty], object]) ?? []).values()];

export const getUniqueValueList = (valueList = []) => Array.from(new Set(valueList));

/**
 * Sort object keys using default Array sort method
 * @param obj
 * @returns {{}}
 */
export const sortObjKeys = ({ obj = {} } = {}) => Object.keys(obj).sort().reduce((sortedObj, nextKey) => ({ ...sortedObj, [nextKey]: obj[nextKey] }), {});

/**
 * Returns tghe value at path of object
 * @param obj
 * @param path
 * @returns {any}
 */
export const getObjectPathValue = ({ obj = {}, path = null } = {}) => get(obj, path);

export const isUUID = (val) => {
    const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    return regexExp.test(val);
};

export const hasValue = (val) => {
    if (val !== null && val !== undefined && val?.toString().trim().length !== 0) {
        return true;
    }
    if (Array.isArray(val) || isObject(val)) {
        return true;
    }

    return false;
};

// Helper to force-convert any format to a comparable number
export const toMillis = (dateStr) => {
    if (!dateStr) return 0;

    // Fix for "2026-04-09:13:13:49" or "09/Apr/2026:13:13:49"
    // Moment/JS hates the colon immediately after the year.
    const cleaned = dateStr.toString().replace(/(\d{4}):(\d{2})/, '$1 $2');

    const m = moment(cleaned);
    return m.isValid() ? m.valueOf() : 0;
};
